import { Fragment, useEffect, useRef, useState } from "react";
import { InboxOutlined } from "@ant-design/icons";
import { Dialog, Transition } from "@headlessui/react";
import { DetailTicket } from "../../../../types/models/ticket.model.type";
import { Form, Input, message } from "antd";
import type { RcFile, UploadFile } from "antd/es/upload/interface";
import { Spinner } from "../../../../components/global/Spinner";
import { useSolveTicketMutation } from "../../../../app/features/sharedTickets/sharedTickets.api";
import { SolveTicketRequest } from "../../../../types/request/ticket.request.type";
import { SolveTicketResponse } from "../../../../types/response/ticket.response.type";
import { useAppDispatch, useAppSelector } from "../../../../hooks/redux.hook";
import { rxCloseModalTicketSolve } from "../../../../app/features/sharedTickets/sharedTickets.slice";
import { rxOpenAlertModal } from "../../../../app/features/common/common.slice";
import { Messages } from "../../../../utils/mapping/messages.mapping";
import { ERROR, SUCCESS } from "../../../../utils/enum/responseStates.enum";
import { useAuth } from "../../../../hooks/useAuth.hook";
import { EMPTY_TEXT } from "../../../../utils/enum/defaultParams.enum";
import { CustomFile, FileFormList } from "../../../../types/forms/ticket.form.type";
import { attendTicketsSelector } from "../../../../app/features/attendTickets/attendTickets.slice";
import { getAttendTickets } from "../../../../app/features/attendTickets/attendTickets.thunk";
import Dragger from "antd/lib/upload/Dragger";

declare type Props = {
  isOpen: boolean;
  onCloseModal: VoidFunction;
  ticket?: DetailTicket | undefined;
};

const ModalTicketSolve = ({ isOpen, onCloseModal, ticket }: Props) => {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const { request } = useAppSelector(attendTicketsSelector);
  const [solveTicket, { isLoading: isLoadingSolveTicket }] = useSolveTicketMutation();

  const [form] = Form.useForm();
  const draggerRef: any = useRef();
  const [fileFormList, setFileFormList] = useState<FileFormList>({
    files: [],
    uploadFiles: [],
  });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const code = event.which || event.keyCode;
      let charCode = String.fromCharCode(code).toLowerCase();
      if ((event.ctrlKey || event.metaKey) && charCode === "v") {
        pasteImageFromClipboard();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const onFinish = async (values: any) => {
    const solveTicketRequest: SolveTicketRequest = {
      idTicket: `${ticket!.periodo}-${ticket!.idTicket}`,
      idDet: ticket!.idDetTicket,
      idTema: ticket!.idTema,
      temaDesc: ticket!.temaDesc,
      idSis: ticket!.idSis,
      sistemaDesc: ticket?.sistemaDesc!,
      idinc: ticket!.idInc,
      incidenciaDesc: ticket?.incidenciaDesc!,
      respuesta: values.comment,
      isEnvioMail: ticket!.isEnvioMail,
      correoUsuario: ticket!.correoUsuario,
      idUsuario: user!.id.toString(),
      responsable: user!.username,
      imagen1bs64: fileFormList?.files[0]?.fileData ? fileFormList.files[0].fileData! : EMPTY_TEXT,
      imagen2bs64: fileFormList?.files[1]?.fileData ? fileFormList.files[1].fileData! : EMPTY_TEXT,
      imagen3bs64: fileFormList?.files[2]?.fileData ? fileFormList.files[2].fileData! : EMPTY_TEXT,
      respImg1: fileFormList?.files[0]?.fileName ? fileFormList.files[0].fileName! : EMPTY_TEXT,
      respImg2: fileFormList?.files[1]?.fileName ? fileFormList.files[1].fileName! : EMPTY_TEXT,
      respImg3: fileFormList?.files[2]?.fileName ? fileFormList.files[2].fileName! : EMPTY_TEXT,
    };
    const solveTicketResponse: SolveTicketResponse = await solveTicket(solveTicketRequest).unwrap();
    if (solveTicketResponse.isSuccess) {
      dispatch(rxCloseModalTicketSolve());
      dispatch(
        rxOpenAlertModal({
          title: Messages.empty,
          message: solveTicketResponse.errorMessage,
          type: SUCCESS,
          callbackOk: () => {},
        })
      );
      dispatch(getAttendTickets(request!));
    } else {
      dispatch(rxCloseModalTicketSolve());
      dispatch(
        rxOpenAlertModal({
          title: Messages.empty,
          message: solveTicketResponse.errorMessage,
          type: ERROR,
          callbackOk: () => {},
        })
      );
    }
  };

  const pasteImageFromClipboard = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      const blobOutput = await clipboardItems[0].getType("image/png");
      const fileList = new DataTransfer();
      fileList.items.add(
        new File([blobOutput], `clipboard_${Date.now().toString()}.png`, {
          lastModified: 1534584790000,
          type: "image/png",
        })
      );

      const dropEvent = new DragEvent("drop", {
        dataTransfer: fileList,
        bubbles: true,
        cancelable: true,
      });

      draggerRef.current?.upload?.uploader.fileInput.dispatchEvent(dropEvent);
    } catch (e) {
      //console.log(e);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onCloseModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                  Resolver Ticket
                </Dialog.Title>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">{ticket?.incidenciaDesc}</p>
                </div>
                <Form className="mt-2" form={form} onFinish={onFinish} layout="vertical" requiredMark="optional">
                  <Form.Item name="attachments" label="Adjunte archivos">
                    <Dragger
                      ref={draggerRef}
                      listType="picture"
                      fileList={fileFormList.uploadFiles}
                      beforeUpload={(file: RcFile) => {
                        const isLt5M = file.size / 1024 / 1024 < 5;
                        if (!isLt5M) {
                          message.warning("Solo se pueden adjuntar archivos de 5 MB como máximo.");
                          return;
                        } else if (fileFormList?.files.length === 3) {
                          message.warning("Solo se pueden adjuntar tres archivos como máximo.");
                          return;
                        } else {
                          const fileForm: CustomFile = {
                            uid: file.uid,
                            fileName: file.name,
                          };
                          const reader = new FileReader();
                          reader.onload = (e) => {
                            fileForm.fileData = e.target!.result?.toString();
                            fileForm.fileData = fileForm.fileData?.split(",")[1];
                          };
                          reader.readAsDataURL(file);
                          const customFile = fileForm;
                          const uploadFile = file as UploadFile;
                          uploadFile.url = URL.createObjectURL(file);
                          const tempFileFormList = fileFormList;
                          tempFileFormList.files.push(customFile);
                          tempFileFormList.uploadFiles.push(uploadFile);
                          setFileFormList(tempFileFormList);
                          return false;
                        }
                      }}
                      onRemove={(file) => {
                        const filesFiltered = fileFormList?.files.filter((e) => e.uid !== file.uid);
                        const uploadFilesFiltered = fileFormList.uploadFiles.filter((e) => e.uid !== file.uid);
                        setFileFormList({ files: filesFiltered, uploadFiles: uploadFilesFiltered });
                      }}
                      maxCount={3}
                    >
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                      </p>
                      <p className="ant-upload-text">Click o arrastra el archivo a esta área para adjuntarlo.</p>
                      <p className="ant-upload-hint">
                        Recuerda que solo puedes subir tres archivos como máximo por incidencia o requerimiento.
                      </p>
                    </Dragger>
                  </Form.Item>
                  <Form.Item name="comment" label="Ingrese Comentario">
                    <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
                  </Form.Item>
                  <div className="mt-4 flex justify-end">
                    <button
                      type="submit"
                      className="mr-5 inline-flex justify-center items-center rounded-md border border-transparent bg-green-100 px-4 py-2 text-sm font-medium text-green-900 hover:bg-green-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                    >
                      {isLoadingSolveTicket && (
                        <div className="space-x-2 flex items-center">
                          <Spinner />
                          <span>Procesando...</span>
                        </div>
                      )}
                      {!isLoadingSolveTicket && (
                        <div>
                          <i className="far fa-check mr-2 fa-fw" />
                          Resolver
                        </div>
                      )}
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center items-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      onClick={onCloseModal}
                    >
                      <i className="far fa-xmark mr-2 fa-fw" />
                      Cancelar
                    </button>
                  </div>
                </Form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ModalTicketSolve;
